import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  getRetailPOSPaymentMethod,
  listRetailPOSPaymentMethods,
} from "./retailPOSPaymentMethodRegistry";

import {
  canSubmitRetailPOSCashPayment,
  resolveRetailPOSCashChange,
} from "./retailPOSCashChange";

import {
  resolveRetailPOSPaymentAllocation,
} from "./retailPOSPaymentAllocation";


function resolveReceiptUuid(
  receipt
) {
  const candidate =
    receipt?.receiptUuid
    ?? receipt?.receipt_uuid
    ?? receipt?.uuid;

  if (
    typeof candidate !== "string"
    || candidate.trim() === ""
  ) {
    return null;
  }

  return candidate.trim();
}


export default function RetailPOSPaymentWorkspace({
  runtime,
  context,
  receiptSnapshot,
  onReceiptSnapshotChange = null,
}) {
  const paymentMethods =
    useMemo(
      () =>
        listRetailPOSPaymentMethods(),
      []
    );

  const [
    paymentMethod,
    setPaymentMethod,
  ] =
    useState(
      paymentMethods[0]?.id
      ?? ""
    );

  const [
    paymentAmount,
    setPaymentAmount,
  ] =
    useState("");

  const [
    cashReceivedInput,
    setCashReceivedInput,
  ] =
    useState("");

  const [
    cashSplitEnabled,
    setCashSplitEnabled,
  ] =
    useState(false);

  const [
    paymentPending,
    setPaymentPending,
  ] =
    useState(false);

  const [
    paymentError,
    setPaymentError,
  ] =
    useState(null);

  const [
    paymentsSnapshot,
    setPaymentsSnapshot,
  ] =
    useState([]);

  const paymentInFlightRef =
    useRef(false);

  const mountedRef =
    useRef(true);

  const receiptUuid =
    resolveReceiptUuid(
      receiptSnapshot
    );


  const selectedPaymentMethod =
    getRetailPOSPaymentMethod(
      paymentMethod
    );

  const isCashPayment =
    selectedPaymentMethod?.kind
      === "cash";

  const amountDue =
    Number(
      receiptSnapshot?.remaining
      ?? receiptSnapshot?.totals?.remaining
      ?? 0
    );

  let cashChange = 0;
  let cashSubmitAllowed = false;

  if (isCashPayment) {
    try {
      const cashAmountDue =
        cashSplitEnabled
          ? Number(
            paymentAmount
          )
          : amountDue;

      cashChange =
        resolveRetailPOSCashChange({
          amountDue:
            cashAmountDue,
          cashReceived:
            cashReceivedInput,
        });

      cashSubmitAllowed =
        canSubmitRetailPOSCashPayment({
          amountDue:
            cashAmountDue,
          cashReceived:
            cashReceivedInput,
        });
    } catch {
      cashChange = 0;
      cashSubmitAllowed = false;
    }
  }


  useEffect(
    () => {
      mountedRef.current =
        true;

      return () => {
        mountedRef.current =
          false;
      };
    },
    []
  );


  useEffect(
    () => {
      let active = true;

      async function loadPayments() {
        if (
          !receiptUuid
          || !runtime
          || typeof runtime.listPayments
            !== "function"
        ) {
          setPaymentsSnapshot([]);
          return;
        }

        try {
          const payments =
            await runtime.listPayments(
              receiptSnapshot,
              context
            );

          if (!active) {
            return;
          }

          setPaymentsSnapshot(
            Array.isArray(payments)
              ? payments
              : []
          );
        } catch {
          if (!active) {
            return;
          }

          setPaymentsSnapshot([]);
        }
      }

      loadPayments();

      return () => {
        active = false;
      };
    },
    [
      context,
      receiptSnapshot,
      receiptUuid,
      runtime,
    ]
  );


  async function handleSubmitPayment(
    event
  ) {
    event.preventDefault();

    if (
      paymentInFlightRef.current
      || !receiptUuid
    ) {
      return;
    }

    const method =
      getRetailPOSPaymentMethod(
        paymentMethod
      );

    if (!method) {
      setPaymentError(
        "Moyen de paiement invalide."
      );

      return;
    }

    let amount;

    try {
      if (isCashPayment) {
        if (
          !Number.isFinite(amountDue)
          || amountDue <= 0
        ) {
          throw new TypeError(
            "Aucun montant espèces n'est dû."
          );
        }

        if (cashSplitEnabled) {
          const allocation =
            resolveRetailPOSPaymentAllocation({
              remaining:
                amountDue,

              requestedAmount:
                paymentAmount,
            });

          amount =
            allocation.amount;

          const cashReceived =
            Number(
              cashReceivedInput
            );

          if (
            !Number.isFinite(
              cashReceived
            )
            || cashReceived < amount
          ) {
            throw new TypeError(
              "Le montant reçu doit couvrir le montant du paiement."
            );
          }
        } else {
          if (!cashSubmitAllowed) {
            throw new TypeError(
              "Le montant reçu doit couvrir le montant dû."
            );
          }

          amount =
            amountDue;
        }
      } else {
        const requestedAmount =
          Number(
            paymentAmount
          );

        if (
          !Number.isFinite(
            requestedAmount
          )
          || requestedAmount <= 0
        ) {
          throw new TypeError(
            "Le montant doit être strictement positif."
          );
        }

        const allocation =
          resolveRetailPOSPaymentAllocation({
            remaining:
              amountDue,

            requestedAmount,
          });

        amount =
          allocation.amount;
      }
    } catch (error) {
      setPaymentError(
        error?.message
        ?? "Montant de paiement invalide."
      );

      return;
    }

    paymentInFlightRef.current =
      true;

    setPaymentPending(true);
    setPaymentError(null);

    try {
      await runtime.submitPayment(
        receiptSnapshot,
        {
          meansPayment:
            method.apiValue,

          amount,

          checkFormat:
            true,
        },
        context
      );

      const [
        authoritativePayments,
        authoritativeReceipt,
      ] =
        await Promise.all([
          runtime.listPayments(
            receiptSnapshot,
            context
          ),

          runtime.resolveReceipt(
            {
              receiptUuid,

              companyUuid:
                context.companyUuid,
            },
            context
          ),
        ]);

      if (!mountedRef.current) {
        return;
      }

      setPaymentsSnapshot(
        Array.isArray(
          authoritativePayments
        )
          ? authoritativePayments
          : []
      );

      if (
        typeof onReceiptSnapshotChange
          === "function"
      ) {
        onReceiptSnapshotChange(
          authoritativeReceipt
        );
      }

      setPaymentAmount("");
      setCashReceivedInput("");
      setCashSplitEnabled(false);
    } catch (error) {
      if (!mountedRef.current) {
        return;
      }

      setPaymentError(
        error?.message
        ?? "Impossible d'enregistrer le paiement."
      );
    } finally {
      paymentInFlightRef.current =
        false;

      if (mountedRef.current) {
        setPaymentPending(false);
      }
    }
  }


  if (!receiptUuid) {
    return null;
  }


  return (
    <section
      className="retail-pos-workspace__panel"
      aria-labelledby="retail-pos-payment-title"
      aria-busy={
        paymentPending
          ? "true"
          : "false"
      }
      data-testid="retail-pos-payment-workspace"
    >
      <header>
        <h3
          id="retail-pos-payment-title"
        >
          Paiement
        </h3>

        <p>
          Les paiements et le Receipt restent
          autoritaires côté Runtime / backend.
        </p>
      </header>

      <form
        onSubmit={
          handleSubmitPayment
        }
      >
        <label>
          Moyen de paiement

          <select
            value={
              paymentMethod
            }
            onChange={
              (event) => {
                const nextMethod =
                  event.target.value;

                setPaymentMethod(
                  nextMethod
                );

                if (
                  getRetailPOSPaymentMethod(
                    nextMethod
                  )?.kind !== "cash"
                ) {
                  setCashReceivedInput("");
                  setCashSplitEnabled(false);
                }

                setPaymentError(null);
              }
            }
            disabled={
              paymentPending
            }
            data-testid="retail-pos-payment-method"
          >
            {paymentMethods.map(
              (method) => (
                <option
                  key={
                    method.id
                  }
                  value={
                    method.id
                  }
                >
                  {method.label}
                </option>
              )
            )}
          </select>
        </label>

        <label>
          Montant

          <input
            type="number"
            min="0.01"
            step="0.01"
            inputMode="decimal"
            value={
              paymentAmount
            }
            onChange={
              (event) => {
                setPaymentAmount(
                  event.target.value
                );

                setPaymentError(null);
              }
            }
            disabled={
              paymentPending
            }
            data-testid="retail-pos-payment-amount"
          />
        </label>

        {isCashPayment && (
          <div
            data-testid="retail-pos-cash-ux"
          >
            <p>
              Montant dû :
              {" "}
              {Number.isFinite(amountDue)
                ? amountDue.toFixed(2)
                : "0.00"}
              €
            </p>

            <label>
              <input
                type="checkbox"
                checked={
                  cashSplitEnabled
                }
                onChange={
                  (event) => {
                    setCashSplitEnabled(
                      event.target.checked
                    );

                    setPaymentError(null);
                  }
                }
                disabled={
                  paymentPending
                }
                data-testid="retail-pos-cash-split-toggle"
              />

              Paiement espèces partiel
            </label>

            {cashSplitEnabled && (
              <p
                data-testid="retail-pos-cash-split-hint"
              >
                Le montant du paiement correspond au montant imputé au Receipt.
              </p>
            )}

            <label>
              Montant reçu

              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={
                  cashReceivedInput
                }
                onChange={
                  (event) => {
                    setCashReceivedInput(
                      event.target.value
                    );

                    setPaymentError(null);
                  }
                }
                disabled={
                  paymentPending
                }
                data-testid="retail-pos-cash-received"
              />
            </label>

            <p
              data-testid="retail-pos-cash-change"
            >
              Monnaie à rendre :
              {" "}
              {cashChange.toFixed(2)}
              €
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={
            paymentPending
            || !paymentMethod
            || (
              isCashPayment
                ? (
                  !cashReceivedInput
                  || (
                    cashSplitEnabled
                    && !paymentAmount
                  )
                )
                : !paymentAmount
            )
          }
          data-testid="retail-pos-payment-submit"
        >
          {paymentPending
            ? "Paiement en cours…"
            : "Enregistrer le paiement"}
        </button>
      </form>

      {paymentPending && (
        <p
          role="status"
          data-testid="retail-pos-payment-pending"
        >
          Paiement en cours…
        </p>
      )}

      {paymentError && (
        <p
          role="alert"
          data-testid="retail-pos-payment-error"
        >
          {paymentError}
        </p>
      )}

      <section
        aria-label="Paiements enregistrés"
        data-testid="retail-pos-payments-snapshot"
      >
        <p>
          Paiements enregistrés :
          {" "}
          {paymentsSnapshot.length}
        </p>
      </section>
    </section>
  );
}
